import React, {
    useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import grandBooth from '../../assets/jss/components/grandBooth';
import CustomTypography from '../../components/Typography/CustomTypography';
import Button from '../../components/Button/CustomButton';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import FormDialog from '../../components/Dialog/FormDialog';
import { MEDIA_TYPE } from '../../common/Constants';
import Snackbar from '../../components/Snackbar/Snackbar';
import { PlayCircleFilled, FileAudio, KeyboardBackspaceSharp } from '../../components/Icons/Icon';

import BusinessContactForm from './BusinessContactForm';
import useCompanyNavigation from './hooks/useCompanyNavigation';
import useCompanyInfo from './hooks/useCompanyInfo';
import GrandBoothProductItem from './GrandBoothProductItem';
import GrandBoothProductItemCustomLayout from './GrandBoothProductItemCustomLayout';

const useStyles = makeStyles(grandBooth);

const GrandBoothProductList = (props) => {
    const {
        productList, companyList, nameEvent, companyId,
        companyInfo,
        totalProducts,
        fetchData,
        product,
        getProduct,
        isAuthorized,
        token,
        queryStr,
        isProductsViewable,
        infoAddToCart,
        addProductToCart,
        clearAddToCart,
        isWeakAuth,
        eventInfo,
        getCount,
        customText,
        customColorExhibitor,
        customTextPersonInfo,
        customTextCompany,
        isPreview,
    } = props;
    const classes = useStyles(props);
    const [productActiveIndex, setProductActiveIndex] = useState(-1);
    const [openBusinessContactForm, setOpenBusinessContactForm] = useState(false);
    const [isOpenSnackBar, setOpenSnackBar] = useState(false);
    const history = useHistory();
    const dynamicLoadNextPage = useRef(false);
    const [isOpenSnackBarPreviewPage, setIsOpenSnackBarPreviewPage] = useState(false);

    const intl = useIntl();

    const {
        isOpenDialogCompanyInfo,
        companyInfoView,
        setIsOpenDialogCompanyInfo,
    } = useCompanyInfo(
        companyInfo,
        customTextCompany,
    );
    const {
        linkPrev,
        linkNext,
    } = useCompanyNavigation(
        companyList,
        companyId,
        nameEvent,
        queryStr,
    );

    useEffect(() => {
        setProductActiveIndex(-1);
    }, [companyInfo]);

    useEffect(() => {
        if (productList && dynamicLoadNextPage.current === true) {
            dynamicLoadNextPage.current = false;
            setProductActiveIndex((...prevState) => parseInt(prevState, 10) + 1);
        }
    }, [productList]);

    useEffect(() => {
        if (productList[productActiveIndex]) {
            getProduct(companyId, productList[productActiveIndex].id);
        }
    }, [productList, companyId, getProduct, productActiveIndex]);

    const isProductDetailMode = () => productActiveIndex !== -1;

    const nextProduct = () => {
        const nextValue = productActiveIndex + 1;
        const currentNumberOfProducts = productList.length;

        if (nextValue === currentNumberOfProducts && currentNumberOfProducts < totalProducts) {
            dynamicLoadNextPage.current = true;
            fetchData();
        }

        if (nextValue < currentNumberOfProducts) {
            setProductActiveIndex(nextValue);
        }
    };

    const prevProduct = () => {
        if (productActiveIndex > 0) setProductActiveIndex(productActiveIndex - 1);
    };

    const handleNextPrev = (href) => {
        history.push(href);
    };

    const renderButtonNextPrev = (title, href, disable, newTitle, backgroundColor) => (
        <Button
            color="navy"
            gradient
            size="sm"
            round
            className={classes.buttonBig}
            onClick={() => handleNextPrev(href)}
            disabled={disable}
            buttonTextColor={eventInfo?.settings_colors_viewer?.button_text_color}
            buttonBackgroundColor={backgroundColor}
        >
            {newTitle || title}
        </Button>
    );

    return (
        <GridContainer>
            {isProductDetailMode() && (
                <GridContainer>
                    <GridItem sm={7} style={{ height: 'auto' }}>
                        <div className={classes.backLink} onClick={() => setProductActiveIndex(-1)} role="button" tabIndex="-1">
                            <KeyboardBackspaceSharp color={customColorExhibitor?.event_name_text_color} />
                            <span>
                                {intl.formatMessage({ id: 'backToList' })}
                            </span>
                        </div>
                    </GridItem>
                    <GridItem sm={5} style={{ display: 'flex', justifyContent: 'flex-start', height: 'auto' }}>
                        <Button
                            color="navy"
                            className={classes.btnShowCompanyInfo}
                            onClick={!isPreview ? () => setIsOpenDialogCompanyInfo(true) : () => setIsOpenSnackBarPreviewPage(true)}
                            buttonTextColor={eventInfo?.settings_colors_viewer?.button_text_color}
                            buttonBackgroundColor={customColorExhibitor?.button_background_color?.common_button}
                        >
                            {customText?.button_name?.exhibitors_detailed_info
                                ? (
                                    <>
                                        {customText?.button_name?.exhibitors_detailed_info.length > 4 ? (
                                            <>
                                                {customText?.button_name?.exhibitors_detailed_info.slice(0, (customText?.button_name?.exhibitors_detailed_info.length) / 2)}
                                                <br />
                                                {customText?.button_name?.exhibitors_detailed_info.slice((customText?.button_name?.exhibitors_detailed_info.length) / 2)}
                                            </>
                                        ) : (
                                            customText?.button_name?.exhibitors_detailed_info
                                        )}
                                    </>
                                )
                                : (
                                    <>
                                        {intl.formatMessage({ id: 'exhibition.detailInfoButton' })}
                                        <br />
                                        {intl.formatMessage({ id: 'exhibition.detailInfoExhibit' })}
                                    </>
                                )}
                        </Button>
                    </GridItem>
                </GridContainer>
            )}
            <GridContainer style={{ alignSelf: 'baseline' }}>
                {(productList.length > 0 && !isProductDetailMode() && !eventInfo.layout_exhibition) ? (
                    productList.map((productItem, index) => (
                        <GridItem sm={4} md={3} lg={2} key={productItem.id}>
                            <article className={classes.productItem} onClick={() => setProductActiveIndex(index)}>
                                <div className={classes.productThumbnail} title={productItem.name}>
                                    {productItem.media_type === MEDIA_TYPE.IMAGE && (
                                        <img
                                            src={productItem.media}
                                            alt={productItem.name}
                                            className={classes.productItemImg}
                                        />
                                    )}
                                    {productItem.media_type === MEDIA_TYPE.AUDIO && (
                                        <span className={classes.productItemIcon}>
                                            <FileAudio color="#f44336" size="36px" />
                                        </span>
                                    )}
                                    {productItem.media_type === MEDIA_TYPE.VIDEO && (
                                        <>
                                            <span className={classes.productItemIcon}>
                                                <PlayCircleFilled color="#f44336" size="36px" />
                                            </span>
                                            {productItem.output_thumb_filepath && (
                                                <img
                                                    src={productItem.output_thumb_filepath}
                                                    alt={productItem.name}
                                                    className={classes.productItemImg}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className={classes.productMeta}>
                                    <div className={classes.productPrice}>
                                        {`${intl.formatNumber(productItem.price)}${intl.formatMessage({ id: 'common.yen' })}`}
                                    </div>
                                    <div className={classes.productItemNameWrapper}>
                                        <h2 className={classes.productItemName} title={productItem.name}>{productItem.name}</h2>
                                    </div>
                                </div>
                            </article>
                        </GridItem>
                    ))
                ) : ''}

                {(productList.length > 0 && !isProductDetailMode() && eventInfo.layout_exhibition) ? (
                    <GrandBoothProductItemCustomLayout
                        product={productList}
                        productActive={(value) => setProductActiveIndex(value)}
                        token={token}
                        customColorExhibitor={customColorExhibitor}
                    />
                ) : ''}

                {isProductDetailMode() && (
                    <GrandBoothProductItem
                        productDetail={productList[productActiveIndex]}
                        outsideLinkUrl={product.outside_link_url}
                        token={token}
                        isAuthorized={isAuthorized}
                        nextProduct={nextProduct}
                        prevProduct={prevProduct}
                        setOpenBusinessContactForm={setOpenBusinessContactForm}
                        productActiveIndex={productActiveIndex}
                        totalProducts={totalProducts}
                        addProductToCart={addProductToCart}
                        clearAddToCart={clearAddToCart}
                        infoAddToCart={infoAddToCart}
                        isWeakAuth={isWeakAuth}
                        eventInfo={eventInfo}
                        getCount={getCount}
                        customText={customText}
                        isPreview={isPreview}
                    />
                )}
            </GridContainer>

            <GridContainer>
                <GridItem xs={12}>
                    {(!productList.length && isProductsViewable) && (
                        <CustomTypography
                            align="center"
                            variant="subtitle1"
                            style={{ marginBottom: '15px' }}
                            customColor={eventInfo?.settings_colors_viewer?.contents_text_color}
                        >
                            {intl.formatMessage({ id: 'exhibition.noProductRegister' })}
                        </CustomTypography>
                    )}
                    {(!isProductsViewable && !isPreview) && (
                        <CustomTypography
                            align="center"
                            variant="subtitle1"
                            style={{ marginBottom: '15px' }}
                            customColor={eventInfo?.settings_colors_viewer?.contents_text_color}
                        >
                            {intl.formatMessage({ id: 'organizerPosterList.pastEventPeriod' })}
                        </CustomTypography>
                    )}

                    {(companyList.length > 0 && !isProductDetailMode() && !isPreview) && (
                        <Box display="flex" alignItems="center" justifyContent="center" flexWrap="wrap" width="100%">
                            {renderButtonNextPrev(
                                intl.formatMessage({ id: 'exhibition.previousCompany' }),
                                linkPrev.link,
                                linkPrev.disable,
                                customText?.button_name?.previous_company,
                                customColorExhibitor?.button_background_color?.common_button,
                            )}
                            {renderButtonNextPrev(
                                intl.formatMessage({ id: 'exhibition.nextCompany' }),
                                linkNext.link,
                                linkNext.disable,
                                customText?.button_name?.next_company,
                                customColorExhibitor?.button_background_color?.common_button,
                            )}
                        </Box>
                    )}
                </GridItem>
            </GridContainer>

            <BusinessContactForm
                isOpen={openBusinessContactForm}
                onClose={() => setOpenBusinessContactForm(false)}
                showSnackBar={() => setOpenSnackBar(true)}
                customColorExhibitor={customColorExhibitor}
                customText={customText}
                customTextPersonInfo={customTextPersonInfo}
            />
            <Snackbar
                open={isOpenSnackBar}
                onClose={() => setOpenSnackBar(false)}
                message={intl.formatMessage({ id: 'exhibition.sendCompleted' })}
                severity="success"
                autoHideDuration={2000}
            />
            <Snackbar
                open={isOpenSnackBarPreviewPage}
                onClose={() => setIsOpenSnackBarPreviewPage(false)}
                message={intl.formatMessage({ id: 'common.noPreview' })}
                severity="error"
                autoHideDuration={1000}
            />
            <FormDialog
                maxWidth="sm"
                isOpen={isOpenDialogCompanyInfo}
                handleClose={() => setIsOpenDialogCompanyInfo(false)}
                title={customText?.button_name?.exhibitors_detailed_info || intl.formatMessage({ id: 'exhibition.exhibitorDetailInfo' })}
                customColor={customColorExhibitor?.contents_text_color}
                paperProps={{
                    style: {
                        maxHeight: '60vh',
                    },
                }}
            >
                {
                    companyInfoView.map((info) => (
                        <GridContainer key={info.label}>
                            <GridItem xs={5} sm={3} md={3}>
                                <CustomTypography bold variant="subtitle1" customColor={eventInfo?.settings_colors_viewer?.contents_text_color}>
                                    {info.label}
                                </CustomTypography>
                            </GridItem>
                            <GridItem xs={7} sm={9} md={9}>
                                <CustomTypography variant="subtitle1" customColor={eventInfo?.settings_colors_viewer?.contents_text_color}>
                                    {info.value}
                                </CustomTypography>
                            </GridItem>
                        </GridContainer>
                    ))
                }
            </FormDialog>
        </GridContainer>
    );
};

GrandBoothProductList.defaultProps = {
    productList: {},
    companyList: [],
    nameEvent: '',
    companyId: '',
    product: {},
    companyInfo: {},
    token: '',
    queryStr: '',
    totalProducts: 0,
    isProductsViewable: false,
    infoAddToCart: {},
    addProductToCart: () => {},
    clearAddToCart: () => {},
    isWeakAuth: {},
    eventInfo: {},
    getCount: () => {},
    customText: {},
    customColorExhibitor: {},
    customTextPersonInfo: {},
    customTextCompany: {},
    isPreview: false,
};

GrandBoothProductList.propTypes = {
    productList: PropTypes.oneOfType([() => null, PropTypes.object]),
    companyList: PropTypes.oneOfType([() => null, PropTypes.array]),
    nameEvent: PropTypes.string,
    companyId: PropTypes.string,
    getProduct: PropTypes.func.isRequired,
    product: PropTypes.oneOfType([() => null, PropTypes.object]),
    companyInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
    totalProducts: PropTypes.number,
    fetchData: PropTypes.func.isRequired,
    isAuthorized: PropTypes.bool.isRequired,
    token: PropTypes.string,
    queryStr: PropTypes.string,
    isProductsViewable: PropTypes.bool,
    infoAddToCart: PropTypes.oneOfType([() => null, PropTypes.object]),
    addProductToCart: PropTypes.func,
    clearAddToCart: PropTypes.func,
    isWeakAuth: PropTypes.oneOfType([() => null, PropTypes.object]),
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
    getCount: PropTypes.func,
    customText: PropTypes.oneOfType([() => null, PropTypes.object]),
    customColorExhibitor: PropTypes.oneOfType([() => null, PropTypes.object]),
    customTextPersonInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
    customTextCompany: PropTypes.oneOfType([() => null, PropTypes.object]),
    isPreview: PropTypes.bool,
};

export default GrandBoothProductList;
